/* eslint-disable consistent-return */
/* eslint-disable prefer-arrow-callback */
/* eslint-disable func-names */
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
    ApolloClient, createHttpLink, InMemoryCache, ApolloProvider, fromPromise,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import posthog from 'posthog-js'
import App from './App';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import config from './config';
import { endSession, getValidAccessToken, refreshAccessToken } from './utils/session';
import InstallButtonContextProvider from './contexts/InstallButton';
import PopupMenuProvider from './contexts/PopupMenu'
import SelectPopupProvider from './contexts/SingleSelect';
import OriginatedPopupProvider from './contexts/OriginatedPopup';
import { ToastProvider } from './contexts/Toast';
import AIChatProvider from './contexts/AIChat';
import { Logger } from './Logger';
import { SW_UPDATED_FLAG } from './utils/serviceWorkerUpdate';

import './index.scss';

// import product analytics tool

posthog.init(config.posthogApiKey, {
    api_host: 'https://eu.posthog.com',
    session_recording: {
        maskTextSelector: '.sensitive',
        maskAllInputs: false,
        maskInputOptions: {
            password: true,
        }
    },
    cross_subdomain_cookie: false
})

/**
 * TODO: find a way to opt out of capturing.
 * Some options are different projects for different environments or gtm.
 */
if (window.location.hostname === 'localhost') {
    posthog.opt_out_capturing()
}

Logger.init()


// Apollo configuration
const httpLink = createHttpLink({
    uri: config.GRAPHQL_ENDPOINT
});
const authLink = setContext(async (_, { headers }) => {
    // Resolves the access token per operation, renewing it from the refresh cookie
    // when it has aged out. Reading localStorage here was enough while tokens lasted
    // a year; at 15 minutes a tab left open over lunch would send an expired one on
    // every query.
    const token = await getValidAccessToken();
    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            Authorization: token ? `Bearer ${token}` : '',
        }
    }
});

// Recovers the operations that were already in flight when the token expired.
//
// authLink renews proactively, so this is the narrow race: a query whose header was
// built a moment before the token lapsed, or one sent while the device clock was
// off. /graphql answers those with a bare 401 from the auth middleware — before
// Apollo Server sees the request — so it arrives as a network error and never as a
// GraphQL error, which is why this matches on statusCode rather than on
// UNAUTHENTICATED.
const authErrorLink = onError(({ networkError, operation, forward }) => {
    if (networkError?.statusCode !== 401) {
        return undefined;
    }

    return fromPromise(
        refreshAccessToken().then((token) => {
            if (token) {
                return token;
            }

            // No session left to renew. Rethrowing keeps the caller's error path
            // intact while the redirect takes over.
            endSession();
            throw networkError;
        })
    ).flatMap((token) => {
        operation.setContext(({ headers = {} }) => ({
            headers: { ...headers, Authorization: `Bearer ${token}` },
        }));

        return forward(operation);
    });
});

const client = new ApolloClient({
    link: authLink.concat(authErrorLink).concat(httpLink),
    cache: new InMemoryCache({ resultCaching: false }),
});

// Moved out of an inline <script> in public/index.html so that the CSP there can
// drop script-src 'unsafe-inline'. Runs before render; React owns #root's
// children, not its style attribute, so this survives mounting.
if (!window.matchMedia('(display-mode: standalone)').matches) {
    document.querySelector('#root').style.cssText = 'height: 100% !important';
}

const root = createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
        <ChunkErrorBoundary>
            <ApolloProvider client={client}>
                <InstallButtonContextProvider>
                    <PopupMenuProvider>
                        <SelectPopupProvider>
                            <OriginatedPopupProvider>
                                <ToastProvider>
                                    <AIChatProvider>
                                        <App />
                                    </AIChatProvider>
                                    <div id="popup-menu-root" />
                                    <div id="single-select-popup-root" />
                                    <div id="originated-popup-root" />
                                    <div id="search-portal-root" />
                                    <div id="main-search-portal-root" />
                                    <div id="toast-root" />
                                </ToastProvider>
                            </OriginatedPopupProvider>
                        </SelectPopupProvider>
                    </PopupMenuProvider>
                </InstallButtonContextProvider>
            </ApolloProvider>
        </ChunkErrorBoundary>
    </React.StrictMode>
);

const LocalServiceWorkerRegister = function () {
    // reload once when the new Service Worker starts activating
    let refreshing
    let activatedWorker
    navigator.serviceWorker?.addEventListener('controllerchange', () => {
        if (refreshing) return
        refreshing = true
        // Leave a marker for the page we are about to load, so the app can tell
        // the user why it reloaded (App.js -> ServiceWorkerUpdateNotifier).
        // 'controllerchange' only fires once the new worker has actually taken
        // over, so the marker is never a false positive.
        try {
            sessionStorage.setItem(SW_UPDATED_FLAG, '1');
        } catch (err) {
            // sessionStorage can throw in private / storage-blocked contexts.
            // The reload still matters more than the toast.
        }
        window.location.reload()
    });

    const swPath = `${process.env.PUBLIC_URL}/sw.js`;
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
        window.addEventListener('load', () => {
            navigator.serviceWorker.register(swPath).then(reg => {
                // Apply a new version as soon as one is ready: tell the waiting
                // worker to skipWaiting, which makes it take over and triggers
                // the 'controllerchange' reload above. The user is told after
                // the fact with a toast rather than being asked up front.
                function activateUpdate(waitingWorker) {
                    if (!waitingWorker) return;
                    // First-ever install is not an update: the page is not yet
                    // controlled, and the fresh worker only sits in `waiting` for
                    // the instant before it activates on its own. Acting there
                    // would reload a page the user has only just opened.
                    if (!navigator.serviceWorker.controller) return;
                    // `waiting` is re-read on every registration and on every
                    // 'updatefound', so only message a given worker once.
                    if (activatedWorker === waitingWorker) return;
                    activatedWorker = waitingWorker;
                    waitingWorker.postMessage('skipWaiting');
                }
                // eslint-disable-next-line @typescript-eslint/no-shadow
                function listenForWaitingServiceWorker(reg) {
                    function awaitStateChange() {
                        // 'updatefound' can fire with nothing installing (e.g. the
                        // worker was discarded), so guard before subscribing.
                        if (!reg.installing) return;
                        reg.installing.addEventListener('statechange', function () {
                            if (this.state === 'installed' && reg.waiting) {
                                activateUpdate(reg.waiting);
                            }
                        })
                    }
                    if (!reg) return
                    // eslint-disable-next-line consistent-return
                    if (reg.waiting) return activateUpdate(reg.waiting);
                    if (reg.installing) awaitStateChange();
                    reg.addEventListener('updatefound', awaitStateChange);
                }
                listenForWaitingServiceWorker(reg) // <-- Added to existing code

            }).catch(err => {
                // eslint-disable-next-line no-console
                console.log('onupdate sw:', err)
            });
            // eslint-disable-next-line consistent-return
            navigator.serviceWorker.ready.then((swRegistration) => {
                if (window.matchMedia('(display-mode: standalone)').matches) {
                    return swRegistration.sync.register('offline-data-sync');
                }
            });

        });
    }
}

LocalServiceWorkerRegister();

window.addEventListener('appinstalled', async () => {
    await window.Notification.requestPermission();
});
